<template>
  <div class="product-detail-page">
    <div class="container">
      <!-- Breadcrumb -->
      <nav class="breadcrumb-inline">
        <a href="/">Home</a>
        <template v-if="product.category">
          <span>/</span>
          <a :href="`/${product.category.slug}`">{{ product.category.name }}</a>
        </template>
        <template v-if="product.subcategory">
          <span>/</span>
          <a :href="`/${product.category?.slug}/${product.subcategory.slug}`">{{ product.subcategory.name }}</a>
        </template>
        <template v-if="product.subsubcategory">
          <span>/</span>
          <a :href="`/${product.category?.slug}/${product.subcategory?.slug}/${product.subsubcategory.slug}`">{{ product.subsubcategory.name }}</a>
        </template>
        <span>/</span>
        <span class="current">{{ product.name }}</span>
      </nav>

      <!-- Main Product Section -->
      <div class="product-main">
        <!-- Left: Image Gallery -->
        <div class="product-gallery">
          <div class="main-image-wrapper">
            <div class="main-image" @click="openLightbox">
              <img :src="currentImage" :alt="product.name" />
              <div class="zoom-hint">
                <i class="fas fa-search-plus"></i>
              </div>
              <span v-if="discountAmount" class="sale-badge">₦{{ formatPrice(discountAmount) }} OFF</span>
            </div>
          </div>
          <div v-if="productImages.length > 1" class="thumbnail-gallery">
            <button
              v-for="(img, index) in productImages"
              :key="index"
              @click="currentImage = img"
              :class="['thumb-btn', { active: currentImage === img }]"
            >
              <img :src="img" :alt="`${product.name} - ${index + 1}`" />
            </button>
          </div>
        </div>

        <!-- Right: Product Info -->
        <div class="product-info">
          <!-- Discount Badge -->
          <div v-if="discount" class="promo-badge">
            <span>ORDER TODAY AND GET {{ discount }}% OFF</span>
          </div>

          <!-- Title -->
          <h1 class="product-title">{{ product.name }}</h1>

          <!-- Short Description (meta_description) -->
          <p v-if="product.meta_description" class="product-short-desc">
            {{ product.meta_description }}
          </p>

          <!-- Rating Summary -->
          <div class="rating-summary">
            <span class="meta-item">
              <i class="fas fa-star"></i> {{ product.average_rating || 0 }}
            </span>
            <span class="meta-item">
              <i class="fas fa-comments"></i> {{ product.reviews_count || 0 }} Reviews
            </span>
            <span class="meta-item">
              <i class="fas fa-eye"></i> {{ product.views || 0 }} views
            </span>
          </div>

          <!-- Product Meta Info -->
          <div class="product-meta-info">
            <span v-if="product.sku" class="meta-item">
              <i class="fas fa-barcode"></i> SKU: {{ product.sku }}
            </span>
            <span class="meta-item">
              <i class="fas fa-undo"></i> 7 Days Return
            </span>
            <span class="meta-item">
              <i class="fas fa-shield-alt"></i> Warranty
            </span>
          </div>

          <!-- Price Section -->
          <div class="price-block">
            <div class="quantity-price-row">
              <div class="quantity-selector-inline">
                <button @click="decreaseQuantity" :disabled="quantity <= 1" class="qty-btn">−</button>
                <span class="qty-display">{{ quantity }}</span>
                <button @click="increaseQuantity" :disabled="quantity >= currentStock" class="qty-btn">+</button>
              </div>
              <div class="price-display">
                <span class="current-price">₦{{ formatPrice(currentPrice) }}</span>
                <span v-if="comparePrice && comparePrice > currentPrice" class="original-price">
                  ₦{{ formatPrice(comparePrice) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Variants Section -->
          <div v-if="hasColorVariants || hasSizeVariants" class="variants-row">
            <div v-if="hasColorVariants" class="variant-group variant-color">
              <label class="variant-label">Color: <strong>{{ selectedColorName }}</strong></label>
              <div class="color-options">
                <button
                  v-for="color in colorVariants"
                  :key="color.value"
                  @click="selectColor(color.value)"
                  :class="['color-swatch', { active: selectedColor === color.value, disabled: !color.available }]"
                  :style="{ backgroundColor: color.hex || color.value }"
                  :title="color.name"
                  :disabled="!color.available"
                >
                  <i v-if="selectedColor === color.value" class="fas fa-check"></i>
                </button>
              </div>
            </div>

            <div v-if="hasSizeVariants" class="variant-group variant-size">
              <label class="variant-label">Size: <strong>{{ selectedSize || 'Select size' }}</strong></label>
              <div class="size-options">
                <button
                  v-for="size in sizeVariants"
                  :key="size.value"
                  @click="selectSize(size.value)"
                  :class="['size-btn', { active: selectedSize === size.value, disabled: !size.available }]"
                  :disabled="!size.available"
                >
                  {{ size.value }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="hasGenericVariants" class="variant-group">
            <label class="variant-label">Options:</label>
            <div class="generic-options">
              <button
                v-for="variant in product.variants"
                :key="variant.id"
                @click="selectedVariant = variant.id"
                :class="['option-btn', { active: selectedVariant === variant.id, disabled: !variant.is_active || variant.quantity <= 0 }]"
                :disabled="!variant.is_active || variant.quantity <= 0"
              >
                <span class="option-name">{{ variant.variant_name }}</span>
                <span class="option-price">₦{{ formatPrice(variant.base_price) }}</span>
                <span v-if="variant.quantity <= 0" class="option-unavailable">Sold out</span>
              </button>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="purchase-section">
            <div class="action-buttons-row">
              <button
                @click="buyNow"
                class="btn-buy-now"
                :disabled="currentStock <= 0 || isAdding || needsVariantSelection"
              >
                <span>Buy Now</span>
                <i class="fas fa-arrow-right"></i>
              </button>
              <button
                @click="addToCart"
                class="btn-add-cart-outline"
                :disabled="currentStock <= 0 || isAdding || needsVariantSelection"
              >
                <i v-if="isAdding" class="fas fa-spinner fa-spin"></i>
                <span v-else>Add to Cart</span>
              </button>
              <button
                @click="toggleWishlist"
                :class="['btn-wishlist', { active: isWishlisted }]"
              >
                <i :class="isWishlisted ? 'fas fa-heart' : 'far fa-heart'"></i>
              </button>
            </div>
            <!-- Wishlist Notice (inline, no popup) -->
            <div v-if="wishlistMessage" class="wishlist-notice" :class="wishlistMessageType">
              <i class="fas fa-info-circle"></i>
              <span>{{ wishlistMessage }}</span>
            </div>
          </div>

        </div>
      </div>

      <!-- Product Tabs Section -->
      <div class="product-tabs-section">
        <div class="tabs-header">
          <button
            :class="['tab-btn', { active: activeTab === 'description' }]"
            @click="activeTab = 'description'"
          >
            Description
          </button>
          <button
            :class="['tab-btn', { active: activeTab === 'specifications' }]"
            @click="activeTab = 'specifications'"
          >
            Specifications
          </button>
          <button
            :class="['tab-btn', { active: activeTab === 'reviews' }]"
            @click="activeTab = 'reviews'"
          >
            Reviews
          </button>
        </div>

        <!-- Description Tab Content -->
        <div v-show="activeTab === 'description'" class="tab-content">
          <div v-if="product.description" class="description-content" v-html="product.description"></div>
          <div v-else class="empty-state">
            <i class="fas fa-file-alt"></i>
            <p>No description available for this product.</p>
          </div>
        </div>

        <!-- Specifications Tab Content -->
        <div v-show="activeTab === 'specifications'" class="tab-content">
          <div v-if="hasSpecifications" class="specifications-content">
            <table class="specs-table">
              <tbody>
                <tr v-if="product.sku">
                  <th>SKU</th>
                  <td>{{ product.sku }}</td>
                </tr>
                <tr v-if="product.weight">
                  <th>Weight</th>
                  <td>{{ product.weight }} kg</td>
                </tr>
                <tr v-if="product.dimensions">
                  <th>Dimensions</th>
                  <td>{{ product.dimensions }}</td>
                </tr>
                <tr v-if="product.material">
                  <th>Material</th>
                  <td>{{ product.material }}</td>
                </tr>
                <tr v-if="product.category?.name">
                  <th>Category</th>
                  <td>{{ product.category.name }}</td>
                </tr>
                <tr v-if="product.subcategory?.name">
                  <th>Subcategory</th>
                  <td>{{ product.subcategory.name }}</td>
                </tr>
                <tr v-if="availableColors.length > 0">
                  <th>Available Colors</th>
                  <td>{{ availableColors.join(', ') }}</td>
                </tr>
                <tr v-if="availableSizes.length > 0">
                  <th>Available Sizes</th>
                  <td>{{ availableSizes.join(', ') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-state">
            <i class="fas fa-list-alt"></i>
            <p>No specifications available for this product.</p>
          </div>
        </div>

        <!-- Reviews Tab Content -->
        <div v-show="activeTab === 'reviews'" class="tab-content">
          <!-- Rating Overview -->
          <div class="reviews-overview">
            <div class="rating-big">
              <div class="stars-big">
                <i v-for="n in 5" :key="n"
                   :class="n <= Math.round(product.average_rating || 0) ? 'fas fa-star' : 'far fa-star'"></i>
              </div>
              <div class="rating-number">{{ product.average_rating || 0 }}</div>
              <div class="rating-text">out of 5 stars</div>
            </div>

            <!-- Rating Distribution Bars -->
            <div class="rating-bars">
              <div v-for="star in [5, 4, 3, 2, 1]" :key="star" class="rating-bar-row">
                <span class="bar-label">{{ star }}</span>
                <div class="bar-track">
                  <div class="bar-fill" :style="{ width: getRatingPercentage(star) + '%' }"></div>
                </div>
                <span class="bar-percent">{{ getRatingPercentage(star) }}%</span>
              </div>
            </div>
          </div>

          <!-- What Customers Are Saying -->
          <div class="reviews-container">
            <div class="reviews-header">
              <h4>What Our Customers Are Saying</h4>
              <button v-if="canReview" class="btn-write-review" @click="showReviewForm = !showReviewForm">
                Write a customer review
              </button>
            </div>

            <!-- Review Form -->
            <div v-if="showReviewForm && canReview" class="review-form-card">
              <h5>Write Your Review</h5>
              <div class="rating-select">
                <span>Your Rating:</span>
                <div class="star-select">
                  <i v-for="n in 5" :key="n"
                     :class="[n <= (reviewHoverRating || reviewRating) ? 'fas fa-star' : 'far fa-star']"
                     @click="setReviewRating(n)"
                     @mouseenter="reviewHoverRating = n"
                     @mouseleave="reviewHoverRating = 0"
                  ></i>
                </div>
              </div>
              <textarea
                v-model="reviewComment"
                placeholder="Share your experience with this product..."
                rows="4"
              ></textarea>
              <button @click="submitReview" :disabled="isSubmittingReview" class="btn-submit-review">
                <i v-if="isSubmittingReview" class="fas fa-spinner fa-spin"></i>
                <span v-else>Submit Review</span>
              </button>
            </div>

            <!-- Reviews List -->
            <div v-if="reviews && reviews.length > 0" class="reviews-list">
              <div v-for="review in reviews" :key="review.id" class="review-card">
                <div class="review-header">
                  <div class="reviewer-info">
                    <div class="reviewer-avatar">
                      {{ (review.user?.first_name || 'A').charAt(0).toUpperCase() }}
                    </div>
                    <div class="reviewer-details">
                      <strong class="reviewer-name">{{ review.user?.first_name || 'Anonymous' }}</strong>
                      <div class="review-rating">
                        <i v-for="n in 5" :key="n"
                           :class="n <= review.rating ? 'fas fa-star' : 'far fa-star'"
                        ></i>
                      </div>
                    </div>
                  </div>
                  <span class="review-date">{{ formatDate(review.created_at) }}</span>
                </div>
                <p class="review-text">{{ review.comment }}</p>
              </div>
            </div>

            <div v-else class="empty-state">
              <i class="fas fa-comments"></i>
              <p>No reviews yet. Be the first to review this product!</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Related Products -->
      <div v-if="limitedRelatedProducts && limitedRelatedProducts.length > 0" class="related-section">
        <h2 class="section-heading">You May Also Like</h2>
        <div class="related-grid">
          <a
            v-for="item in limitedRelatedProducts"
            :key="item.id"
            :href="'/' + item.slug"
            class="related-card"
          >
            <div class="related-image">
              <img :src="item.image" :alt="item.name" loading="lazy" />
            </div>
            <div class="related-details">
              <h3 class="related-name">{{ item.name }}</h3>
              <div class="related-rating">
                <div class="stars">
                  <i v-for="n in 5" :key="n"
                     :class="n <= Math.round(item.average_rating || item.rating || 0) ? 'fas fa-star' : 'far fa-star'"></i>
                </div>
                <span class="rating-value">{{ item.average_rating || item.rating || 0 }}</span>
                <span class="rating-count">({{ item.reviews_count || item.reviews || 0 }})</span>
              </div>
              <div class="related-price">
                <span class="price">₦{{ formatPrice(item.price) }}</span>
                <span v-if="item.original_price && item.original_price > item.price" class="old-price">
                  ₦{{ formatPrice(item.original_price) }}
                </span>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  relatedProducts: {
    type: Array,
    default: () => []
  },
  user: {
    type: Object,
    default: null
  },
  reviews: {
    type: Array,
    default: () => []
  }
})

// Check if user is logged in (from session via props)
const isLoggedIn = computed(() => !!props.user)

const quantity = ref(1)
const selectedVariant = ref(null)
const selectedColor = ref(null)
const selectedSize = ref(null)
const isAdding = ref(false)
const isWishlisted = ref(false)
const wishlistMessage = ref('')
const wishlistMessageType = ref('info')
const activeTab = ref('description')
const currentImage = ref(props.product?.main_image || props.product?.image || '/images/placeholder.jpg')

// Review form state
const reviewRating = ref(0)
const reviewHoverRating = ref(0)
const reviewComment = ref('')
const reviewImages = ref([])
const reviewImagePreviews = ref([])
const isSubmittingReview = ref(false)
const filterRating = ref(0)
const reviewSort = ref('newest')
const reviewPage = ref(1)
const reviewsPerPage = 5
const helpfulReviews = ref([])
const showReviewForm = ref(false)

// Product highlights from meta_description (split by periods or commas)
const productHighlights = computed(() => {
  if (!props.product.meta_description) return []
  // Try to extract bullet points or sentences
  const desc = props.product.meta_description
  // Split by common delimiters and filter empty strings
  const parts = desc.split(/[.•\n]/).map(s => s.trim()).filter(s => s.length > 10 && s.length < 100)
  return parts.slice(0, 4) // Max 4 highlights
})

const ratingLabels = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent'
}

const hasUserReviewed = computed(() => {
  if (!props.user || !props.product.reviews) return false
  return props.product.reviews.some(r => r.user_id === props.user.id)
})

// Allow review if logged in and hasn't reviewed yet (review goes to pending)
const canReview = computed(() => {
  return isLoggedIn.value && !hasUserReviewed.value
})

const csrfToken = computed(() => document.querySelector('meta[name="csrf-token"]')?.content || '')
const currentUrl = computed(() => window.location.href)

const productImages = computed(() => {
  const images = []

  // Check for main_image first, then fall back to image
  const mainImg = props.product.main_image || props.product.image
  if (mainImg) {
    images.push(mainImg)
  }

  if (props.product.product_images && Array.isArray(props.product.product_images)) {
    props.product.product_images.forEach(img => {
      if (img && !images.includes(img)) {
        images.push(img)
      }
    })
  }

  return images.length > 0 ? images : ['/images/placeholder.jpg']
})

const colorMap = {
  'black': '#000000', 'white': '#FFFFFF', 'red': '#DC2626', 'blue': '#2563EB',
  'green': '#16A34A', 'yellow': '#EAB308', 'orange': '#EA580C', 'purple': '#9333EA',
  'pink': '#EC4899', 'gray': '#6B7280', 'grey': '#6B7280', 'brown': '#92400E',
  'navy': '#1E3A5F', 'gold': '#D4AF37', 'silver': '#C0C0C0', 'beige': '#F5F5DC'
}

const hasColorVariants = computed(() => {
  return props.product.variants?.some(v => v.color) || false
})

const hasSizeVariants = computed(() => {
  return props.product.variants?.some(v => v.size) || false
})

const hasGenericVariants = computed(() => {
  if (!props.product.variants || props.product.variants.length === 0) return false
  return !hasColorVariants.value && !hasSizeVariants.value
})

// Specifications computed properties
const hasSpecifications = computed(() => {
  return props.product.sku || props.product.weight ||
         props.product.dimensions || props.product.material ||
         props.product.category?.name || availableColors.value.length > 0 ||
         availableSizes.value.length > 0
})

const availableColors = computed(() => {
  if (!props.product.variants) return []
  const colors = [...new Set(props.product.variants.filter(v => v.color).map(v => v.color))]
  return colors
})

const availableSizes = computed(() => {
  if (!props.product.variants) return []
  const sizes = [...new Set(props.product.variants.filter(v => v.size).map(v => v.size))]
  return sizes
})

const colorVariants = computed(() => {
  if (!hasColorVariants.value) return []
  const colors = new Map()
  props.product.variants.forEach(v => {
    if (v.color && !colors.has(v.color)) {
      colors.set(v.color, {
        value: v.color,
        name: v.color,
        hex: colorMap[v.color.toLowerCase()] || v.color,
        available: v.is_active && v.quantity > 0
      })
    }
  })
  return Array.from(colors.values())
})

const sizeVariants = computed(() => {
  if (!hasSizeVariants.value) return []
  const sizes = new Map()
  props.product.variants.forEach(v => {
    if (v.size && !sizes.has(v.size)) {
      const isAvailable = selectedColor.value
        ? props.product.variants.some(vr => vr.size === v.size && vr.color === selectedColor.value && vr.is_active && vr.quantity > 0)
        : v.is_active && v.quantity > 0
      sizes.set(v.size, { value: v.size, available: isAvailable })
    }
  })
  return Array.from(sizes.values())
})

const selectedColorName = computed(() => selectedColor.value || 'Select color')

const unitPrice = computed(() => {
  if (selectedVariant.value && props.product.variants) {
    const variant = props.product.variants.find(v => v.id === selectedVariant.value)
    return variant?.base_price || props.product.base_price
  }
  return props.product.base_price
})

const currentPrice = computed(() => {
  return unitPrice.value * quantity.value
})

const comparePrice = computed(() => {
  if (selectedVariant.value && props.product.variants) {
    const variant = props.product.variants.find(v => v.id === selectedVariant.value)
    return variant?.compare_price || props.product.compare_price
  }
  return props.product.compare_price
})

const discount = computed(() => {
  if (comparePrice.value && comparePrice.value > unitPrice.value) {
    return Math.round(((comparePrice.value - unitPrice.value) / comparePrice.value) * 100)
  }
  return null
})

const discountAmount = computed(() => {
  if (comparePrice.value && comparePrice.value > unitPrice.value) {
    return comparePrice.value - unitPrice.value
  }
  return null
})

const currentStock = computed(() => {
  if (selectedVariant.value && props.product.variants) {
    const variant = props.product.variants.find(v => v.id === selectedVariant.value)
    return variant ? variant.quantity : 0
  }

  if (props.product.variants && props.product.variants.length > 0) {
    return props.product.variants.reduce((sum, v) => sum + (v.quantity || 0), 0)
  }

  return props.product.is_active ? 999 : 0
})

const stockClass = computed(() => {
  if (currentStock.value <= 0) return 'out-of-stock'
  if (currentStock.value < 10) return 'low-stock'
  return 'in-stock'
})

const stockText = computed(() => {
  if (currentStock.value <= 0) return 'Out of Stock'
  if (currentStock.value < 10) return `Only ${currentStock.value} left in stock`
  return 'In Stock'
})

const stockIcon = computed(() => {
  if (currentStock.value <= 0) return 'fas fa-times-circle'
  if (currentStock.value < 10) return 'fas fa-exclamation-circle'
  return 'fas fa-check-circle'
})

const needsVariantSelection = computed(() => {
  if (!props.product.variants || props.product.variants.length === 0) return false
  if (hasColorVariants.value && !selectedColor.value) return true
  if (hasSizeVariants.value && !selectedSize.value) return true
  if (hasGenericVariants.value && !selectedVariant.value) return true
  return false
})

const addToCartText = computed(() => {
  if (isAdding.value) return 'Adding...'
  if (currentStock.value <= 0) return 'Out of Stock'
  if (needsVariantSelection.value) return 'Select Options'
  return 'Add to Cart'
})

const selectColor = (color) => {
  selectedColor.value = color
  if (selectedSize.value && hasSizeVariants.value) {
    const stillAvailable = props.product.variants?.some(vr => vr.size === selectedSize.value && vr.color === color && vr.is_active && vr.quantity > 0)
    if (!stillAvailable) selectedSize.value = null
  }
  updateSelectedVariant()
}

const selectSize = (size) => {
  selectedSize.value = size
  updateSelectedVariant()
}

const updateSelectedVariant = () => {
  if (!props.product.variants) return

  const variant = props.product.variants.find(v => {
    if (hasColorVariants.value && v.color !== selectedColor.value) return false
    if (hasSizeVariants.value && v.size !== selectedSize.value) return false
    return v.is_active
  })

  selectedVariant.value = variant?.id || null

  if (variant?.variant_image) {
    currentImage.value = variant.variant_image
  }
}

const increaseQuantity = () => {
  if (quantity.value < currentStock.value) quantity.value++
}

const decreaseQuantity = () => {
  if (quantity.value > 1) quantity.value--
}

const openLightbox = () => {
  // Lightbox functionality can be added here
}

const showWishlistNotice = (message, type = 'info') => {
  wishlistMessage.value = message
  wishlistMessageType.value = type
  setTimeout(() => {
    wishlistMessage.value = ''
  }, 3000)
}

const toggleWishlist = async () => {
  // Check if user is logged in
  if (!isLoggedIn.value) {
    showWishlistNotice('Please login to add items to your wishlist', 'info')
    return
  }

  try {
    const response = await fetch(`/wishlist/${props.product.id}`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken.value
      },
      credentials: 'same-origin'
    })

    if (response.ok) {
      const data = await response.json()
      if (data.success) {
        isWishlisted.value = data.wishlisted
        showWishlistNotice(
          data.wishlisted ? 'Added to wishlist' : 'Removed from wishlist',
          'success'
        )
      }
    } else {
      showWishlistNotice('Failed to update wishlist', 'error')
    }
  } catch (error) {
    showWishlistNotice('Failed to update wishlist', 'error')
  }
}

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href)
    if (window.Swal) {
      window.Swal.fire({
        icon: 'success',
        title: 'Link Copied!',
        timer: 1500,
        showConfirmButton: false,
        position: 'top-end',
        toast: true
      })
    }
  } catch (error) {
    // Copy failed silently
  }
}

const addToCart = async () => {
  if (needsVariantSelection.value) {
    if (window.Swal) {
      window.Swal.fire({
        icon: 'warning',
        title: 'Select Options',
        text: 'Please select all product options before adding to cart.',
        confirmButtonColor: '#FD6600'
      })
    }
    return
  }

  isAdding.value = true

  try {
    const formData = new FormData()
    formData.append('_token', csrfToken.value)
    formData.append('quantity', quantity.value)
    if (selectedVariant.value) {
      formData.append('variant_id', selectedVariant.value)
    }

    const response = await fetch(`/cart/${props.product.id}`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      if (window.Swal) {
        window.Swal.fire({
          icon: 'success',
          title: 'Added to Cart!',
          text: data.message,
          timer: 2000,
          showConfirmButton: false,
          position: 'top-end',
          toast: true
        })
      }

      // Dispatch event to update cart count in AppHeader
      if (data.cart_count !== undefined) {
        window.dispatchEvent(new CustomEvent('cart-updated', {
          detail: { count: data.cart_count }
        }))
      }
    } else {
      throw new Error(data.message || 'Failed to add to cart')
    }
  } catch (error) {
    if (window.Swal) {
      window.Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to add product to cart',
        confirmButtonColor: '#FD6600'
      })
    }
  } finally {
    isAdding.value = false
  }
}

const buyNow = async () => {
  if (needsVariantSelection.value) {
    if (window.Swal) {
      window.Swal.fire({
        icon: 'warning',
        title: 'Select Options',
        text: 'Please select all product options before purchasing.',
        confirmButtonColor: '#FD6600'
      })
    }
    return
  }

  isAdding.value = true

  try {
    const formData = new FormData()
    formData.append('_token', csrfToken.value)
    formData.append('quantity', quantity.value)
    if (selectedVariant.value) {
      formData.append('variant_id', selectedVariant.value)
    }

    const response = await fetch(`/cart/${props.product.id}`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      // Redirect to checkout
      window.location.href = '/checkout'
    } else {
      throw new Error(data.message || 'Failed to add to cart')
    }
  } catch (error) {
    if (window.Swal) {
      window.Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to proceed to checkout',
        confirmButtonColor: '#FD6600'
      })
    }
  } finally {
    isAdding.value = false
  }
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-NG').format(price)
}

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// Check wishlist status on mount
const checkWishlistStatus = async () => {
  if (!isLoggedIn.value) return

  try {
    const response = await fetch(`/wishlist/check/${props.product.id}`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      credentials: 'same-origin'
    })

    if (response.ok) {
      const data = await response.json()
      isWishlisted.value = data.wishlisted || false
    }
  } catch (error) {
    // Wishlist check failed silently
  }
}

const setReviewRating = (rating) => {
  reviewRating.value = rating
}

// Rating breakdown helpers
const getReviewsArray = () => {
  // Handle both array and object formats
  let reviews = props.reviews || props.product.reviews || []
  // If it's an object (Laravel Collection), convert to array
  if (reviews && typeof reviews === 'object' && !Array.isArray(reviews)) {
    reviews = Object.values(reviews)
  }
  return Array.isArray(reviews) ? reviews : []
}

const getRatingCount = (star) => {
  const reviewsArray = getReviewsArray()
  if (reviewsArray.length === 0) return 0
  return reviewsArray.filter(r => Number(r.rating) === Number(star)).length
}

const getRatingPercentage = (star) => {
  const reviewsArray = getReviewsArray()
  if (reviewsArray.length === 0) return 0
  const count = getRatingCount(star)
  return Math.round((count / reviewsArray.length) * 100)
}

// Filtered and sorted reviews
const filteredReviews = computed(() => {
  const reviewsArray = getReviewsArray()
  if (reviewsArray.length === 0) return []

  let reviews = [...reviewsArray]

  // Filter by rating
  if (filterRating.value !== 0) {
    reviews = reviews.filter(r => r.rating === filterRating.value)
  }

  // Sort reviews
  switch (reviewSort.value) {
    case 'oldest':
      reviews.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      break
    case 'highest':
      reviews.sort((a, b) => b.rating - a.rating)
      break
    case 'lowest':
      reviews.sort((a, b) => a.rating - b.rating)
      break
    case 'newest':
    default:
      reviews.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  }

  return reviews
})

// Paginated reviews
const totalReviewPages = computed(() => {
  return Math.ceil(filteredReviews.value.length / reviewsPerPage)
})

const paginatedReviews = computed(() => {
  const start = (reviewPage.value - 1) * reviewsPerPage
  const end = start + reviewsPerPage
  return filteredReviews.value.slice(start, end)
})

// Reset page when filter changes
const resetReviewPage = () => {
  reviewPage.value = 1
}

// Image upload handlers
const handleReviewImages = (event) => {
  const files = Array.from(event.target.files)
  const maxFiles = 5 - reviewImages.value.length
  const validFiles = files.slice(0, maxFiles).filter(file => {
    if (!file.type.startsWith('image/')) {
      return false
    }
    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      if (window.Swal) {
        window.Swal.fire({
          icon: 'warning',
          title: 'File Too Large',
          text: `${file.name} exceeds 5MB limit`,
          confirmButtonColor: '#FD6600'
        })
      }
      return false
    }
    return true
  })

  validFiles.forEach(file => {
    reviewImages.value.push(file)
    const reader = new FileReader()
    reader.onload = (e) => {
      reviewImagePreviews.value.push(e.target.result)
    }
    reader.readAsDataURL(file)
  })

  event.target.value = '' // Reset input
}

const removeReviewImage = (index) => {
  reviewImages.value.splice(index, 1)
  reviewImagePreviews.value.splice(index, 1)
}

// Open review image in lightbox
const openReviewImage = (images, index) => {
  if (window.Swal) {
    window.Swal.fire({
      imageUrl: images[index],
      imageAlt: 'Review image',
      showConfirmButton: false,
      showCloseButton: true,
      width: 'auto',
      background: '#000',
      customClass: {
        image: 'review-lightbox-image'
      }
    })
  }
}

// Mark review as helpful
const markHelpful = async (reviewId) => {
  if (helpfulReviews.value.includes(reviewId)) return

  try {
    const response = await fetch(`/reviews/${reviewId}/helpful`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken.value
      },
      credentials: 'same-origin'
    })

    if (response.ok) {
      helpfulReviews.value.push(reviewId)
      // Update local review count
      const review = props.product.reviews?.find(r => r.id === reviewId)
      if (review) {
        review.helpful_count = (review.helpful_count || 0) + 1
      }
    }
  } catch (error) {
    // Silent fail
  }
}

// Report review
const reportReview = async (reviewId) => {
  if (window.Swal) {
    const { value: reason } = await window.Swal.fire({
      title: 'Report Review',
      input: 'select',
      inputOptions: {
        'spam': 'Spam or fake review',
        'inappropriate': 'Inappropriate content',
        'offensive': 'Offensive language',
        'other': 'Other'
      },
      inputPlaceholder: 'Select a reason',
      showCancelButton: true,
      confirmButtonColor: '#FD6600',
      inputValidator: (value) => {
        if (!value) {
          return 'Please select a reason'
        }
      }
    })

    if (reason) {
      try {
        await fetch(`/reviews/${reviewId}/report`, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken.value
          },
          credentials: 'same-origin',
          body: JSON.stringify({ reason })
        })

        window.Swal.fire({
          icon: 'success',
          title: 'Report Submitted',
          text: 'Thank you for your feedback. We will review this report.',
          timer: 2000,
          showConfirmButton: false
        })
      } catch (error) {
        // Silent fail
      }
    }
  }
}

const submitReview = async () => {
  if (!reviewRating.value || !reviewComment.value.trim()) {
    if (window.Swal) {
      window.Swal.fire({
        icon: 'warning',
        title: 'Incomplete Review',
        text: 'Please provide a rating and comment.',
        confirmButtonColor: '#FD6600'
      })
    }
    return
  }

  isSubmittingReview.value = true

  try {
    const formData = new FormData()
    formData.append('rating', reviewRating.value)
    formData.append('comment', reviewComment.value)

    // Append images
    reviewImages.value.forEach((file, index) => {
      formData.append(`images[${index}]`, file)
    })

    const response = await fetch(`/products/${props.product.id}/reviews`, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': csrfToken.value
      },
      credentials: 'same-origin',
      body: formData
    })

    const data = await response.json()

    if (data.success) {
      if (window.Swal) {
        window.Swal.fire({
          icon: 'success',
          title: 'Review Submitted!',
          text: 'Thank you for your review. It will be visible after approval.',
          timer: 2500,
          showConfirmButton: false
        })
      }
      // Reload page to show new review
      setTimeout(() => window.location.reload(), 2500)
    } else {
      throw new Error(data.message || 'Failed to submit review')
    }
  } catch (error) {
    if (window.Swal) {
      window.Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to submit review',
        confirmButtonColor: '#FD6600'
      })
    }
  } finally {
    isSubmittingReview.value = false
  }
}

// Limit related products to 6 from same category
const limitedRelatedProducts = computed(() => {
  if (!props.relatedProducts) return []
  return props.relatedProducts.slice(0, 6)
})

onMounted(() => {
  checkWishlistStatus()

  // Auto-preselect first available color
  if (props.product.variants && props.product.variants.length > 0) {
    const availableColorVariant = props.product.variants.find(v => v.color && v.is_active && v.quantity > 0)
    if (availableColorVariant) {
      selectedColor.value = availableColorVariant.color
    }

    // Auto-preselect first available size
    const availableSizeVariant = props.product.variants.find(v => v.size && v.is_active && v.quantity > 0)
    if (availableSizeVariant) {
      selectedSize.value = availableSizeVariant.size
    }

    // Find and set the matching variant
    if (selectedColor.value || selectedSize.value) {
      const matchingVariant = props.product.variants.find(v => {
        const colorMatch = !selectedColor.value || v.color === selectedColor.value
        const sizeMatch = !selectedSize.value || v.size === selectedSize.value
        return colorMatch && sizeMatch && v.is_active && v.quantity > 0
      })
      if (matchingVariant) {
        selectedVariant.value = matchingVariant.id
      }
    }
  }
})

// Reset pagination when filter or sort changes
watch([filterRating, reviewSort], () => {
  reviewPage.value = 1
})
</script>
